import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";
import { namedInteractions } from "../../app/namedInteractions";

import type { Entry } from "../../api/schemas/entries";
import type { RecordSummary } from "../../api/schemas/records";
import { Route, linkHref } from "../../app/routes";
import type { Capture } from "../../core/captureMatch";
import { lastEntryOf, openTasksOf } from "../../core/recordMemory";
import { i18next } from "../../i18n";
import { avatarView } from "../../widgets/avatar";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import * as Picker from "../../widgets/recordPicker";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Action, Model, Selected } from "./model";

const t = (key: string, options?: Record<string, string>) =>
	i18next.t(`capture.${key}`, options ?? {});

const CAPTURED_KEYS = [
	"name",
	"headline",
	"position",
	"organisation",
	"location",
] as const;

/** Every captured value renders as text; `url` is a link only because it passed validation; `photo` is never a `src`. */
const capturedView = (h: HtmlBuilder<Message>, capture: Capture): Html =>
	h.section(
		[
			h.DataAttribute("testid", "capture-fields"),
			h.AriaLabel(t("captured")),
			h.Class("rounded-2xl border border-base-300 bg-base-100 p-4"),
		],
		[
			h.h2([h.Class("font-semibold mb-2")], [t("captured")]),
			h.dl(
				[h.Class("grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm")],
				[
					...CAPTURED_KEYS.flatMap((key) => {
						const value = capture[key];
						return value === undefined
							? []
							: [
									h.dt([h.Class("text-base-content/60")], [t(`field.${key}`)]),
									h.dd([h.Class("break-words")], [value]),
								];
					}),
					h.dt([h.Class("text-base-content/60")], [t("field.url")]),
					h.dd(
						[h.Class("break-all")],
						[
							h.a(
								[
									h.Href(capture.url),
									h.Target("_blank"),
									h.DataAttribute(
										"analytics-event",
										namedInteractions.captureProfileLink,
									),
									h.Rel("noopener noreferrer"),
									h.Class("link"),
								],
								[capture.url],
							),
						],
					),
					...(capture.photo === undefined
						? []
						: [
								h.dt([h.Class("text-base-content/60")], [t("field.photo")]),
								h.dd([], [t("photoCaptured")]),
							]),
				],
			),
		],
	);

const pickerView = (h: HtmlBuilder<Message>, model: Model): Html =>
	h.submodel({
		slotId: "capture-record-picker",
		view: Picker.view,
		model: model.picker,
		toParentMessage: (message: Picker.Message) =>
			Message.GotPickerMessage({ message }),
		viewInputs: {},
	});

const actionButton = (
	h: HtmlBuilder<Message>,
	model: Model,
	action: Action,
	label: string,
	busyLabel: string,
	message: Message,
	className: string,
): Html => {
	const busy = Option.contains(model.inFlight, action);
	return h.button(
		[
			h.Type("button"),
			h.Class(`btn ${className}`),
			h.AriaLabel(label),
			...busyAttributes(
				h,
				busy,
				Option.isSome(model.inFlight) ||
					(action !== "create" && !AsyncData.isSuccess(model.record)),
			),
			h.OnClick(message),
		],
		[busySpinner(h, busy), busy ? busyLabel : label],
	);
};

/** R4/R5: nothing resolved, so search by hand; Create needs captured fields. */
const unselectedView = (h: HtmlBuilder<Message>, model: Model): Html =>
	h.div(
		[h.Class("space-y-4")],
		Option.match(model.payload, {
			onNone: () => [
				h.div(
					[h.Role("status"), h.Class("alert alert-info")],
					[h.span([], [t("unreadable")])],
				),
				pickerView(h, model),
			],
			onSome: (capture) => [
				capturedView(h, capture),
				h.p([h.Class("text-sm text-base-content/70")], [t("noMatch")]),
				pickerView(h, model),
				actionButton(
					h,
					model,
					"create",
					t("createNew"),
					t("creating"),
					Message.ClickedCreate(),
					"btn-primary",
				),
			],
		}),
	);

const avatar = (h: HtmlBuilder<Message>, model: Model, name: string): Html =>
	avatarView(h, model.image, name, {
		size: "w-16 h-16",
		textSize: "text-2xl",
		alt: t("avatarAlt", { name }),
	});

/** AE10: a name-only match shows what the record currently says and a way out. */
const nameMatchView = (
	h: HtmlBuilder<Message>,
	record: RecordSummary,
): ReadonlyArray<Html> => [
	h.span([h.Class("badge badge-warning")], [t("matchedByName")]),
	h.p(
		[h.Class("text-sm text-base-content/70")],
		[
			[
				record.attributes?.position,
				record.attributes?.organisation,
				record.attributes?.location,
			]
				.filter((value) => value !== undefined && value !== "")
				.join(" · "),
		],
	),
	h.button(
		[
			h.Type("button"),
			h.Class("btn btn-ghost btn-sm"),
			h.AriaLabel(t("notThisPerson")),
			h.OnClick(Message.ClickedNotThisPerson()),
		],
		[t("notThisPerson")],
	),
];

const recordCard = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	selected: Selected,
	record: RecordSummary,
): Html => {
	const capture = Option.getOrNull(model.payload);
	const needsLink =
		capture !== null && record.externalId?.linkedin !== capture.url;
	return h.section(
		[
			h.DataAttribute("testid", "capture-record"),
			h.AriaLabel(record.name),
			h.Class("rounded-2xl border border-base-300 bg-base-100 p-4 space-y-3"),
		],
		[
			h.div(
				[h.Class("flex items-center gap-4")],
				[
					avatar(h, model, record.name),
					h.div(
						[h.Class("min-w-0")],
						[
							h.h2([h.Class("text-lg font-bold break-words")], [record.name]),
							selected.by === "url"
								? h.span([h.Class("badge badge-ghost")], [t("matchedByUrl")])
								: h.empty,
							h.a(
								[
									h.Href(
										linkHref(
											shared.route,
											Route.Record({ id: record.id, asOf: Option.none() }),
										),
									),
									h.Class("link link-primary text-sm block"),
								],
								[t("openRecord")],
							),
						],
					),
				],
			),
			...(selected.by === "name" ? nameMatchView(h, record) : []),
			model.conflict
				? h.p([h.Role("status"), h.Class("text-sm")], [t("conflict")])
				: h.empty,
			model.stale
				? h.p([h.Role("status"), h.Class("text-sm")], [t("stale")])
				: h.empty,
			Option.match(model.synced, {
				onNone: () => h.empty,
				onSome: (fields) =>
					h.p(
						[h.Role("status"), h.Class("text-sm text-success")],
						[
							t("synced", {
								fields: fields.map((f) => t(`field.${f}`)).join(", "),
							}),
						],
					),
			}),
			model.photoFailed
				? h.div(
						[h.Role("alert"), h.Class("alert alert-warning text-sm")],
						[h.span([], [t("photoFailed")])],
					)
				: h.empty,
			capture === null
				? h.empty
				: h.div(
						[h.Class("flex flex-wrap gap-2")],
						[
							needsLink
								? actionButton(
										h,
										model,
										"link",
										t("link"),
										t("linking"),
										Message.ClickedLink(),
										"btn-outline",
									)
								: h.empty,
							actionButton(
								h,
								model,
								"sync",
								t("sync"),
								t("syncing"),
								Message.ClickedSync(),
								"btn-primary",
							),
						],
					),
		],
	);
};

const snippet = (entry: Entry): string =>
	entry.title.trim() ||
	(entry.description.split("\n")[0] ?? "").slice(0, 80).trim();

/** R12/R13: read-only and announced when it appears. */
const memoryView = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
): Html => {
	const record = AsyncData.getOrElse(model.record, () => null);
	const entries = AsyncData.getOrElse(model.entries, () => null);
	const last = entries === null ? undefined : lastEntryOf(entries);
	const tasks = entries === null ? [] : openTasksOf(entries);
	return h.section(
		[
			h.DataAttribute("testid", "capture-memory"),
			h.AriaLive("polite"),
			h.AriaLabel(t("memory.title")),
			h.Class("rounded-2xl border border-base-300 bg-base-100 p-4 space-y-3"),
		],
		[
			h.h2([h.Class("font-semibold")], [t("memory.title")]),
			h.h3([h.Class("text-sm text-base-content/60")], [t("memory.lastEntry")]),
			AsyncData.isFailure(model.entries)
				? h.p([h.Class("text-sm text-error")], [t("memory.failed")])
				: entries === null
					? h.p([h.Class("text-sm")], [i18next.t("common.loading")])
					: last === undefined
						? h.p([h.Class("text-sm")], [t("memory.noEntries")])
						: h.p(
								[h.Class("text-sm")],
								[
									h.time(
										[h.Class("text-base-content/60 mr-2")],
										[new Date(last.timestamp).toLocaleDateString()],
									),
									h.a(
										[
											h.Href(
												linkHref(
													shared.route,
													Route.Entry({ id: last.id, asOf: Option.none() }),
												),
											),
											h.Class("link"),
										],
										[snippet(last)],
									),
								],
							),
			h.h3([h.Class("text-sm text-base-content/60")], [t("memory.openTasks")]),
			tasks.length === 0
				? h.p([h.Class("text-sm")], [t("memory.noOpenTasks")])
				: h.ul(
						[h.Class("list-disc list-inside text-sm")],
						tasks.map((task) => h.li([], [task.text])),
					),
			h.h3([h.Class("text-sm text-base-content/60")], [t("memory.cadence")]),
			record === null
				? h.empty
				: h.p(
						[h.Class("text-sm flex items-center gap-2")],
						[
							record.cadence === undefined
								? i18next.t("record.cadence.none")
								: i18next.t(`keepInTouch.cadence.${record.cadence}`),
							record.paused
								? h.span([h.Class("badge badge-ghost")], [t("memory.paused")])
								: h.empty,
						],
					),
		],
	);
};

const selectedView = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	selected: Selected,
): Html =>
	h.div(
		[h.Class("space-y-4")],
		[
			AsyncData.isFailure(model.record)
				? h.div(
						[h.Role("alert"), h.Class("alert alert-error")],
						[h.span([], [i18next.t("record.failedToLoad")])],
					)
				: Option.match(AsyncData.getData(model.record), {
						onNone: () => h.span([h.Class("loading loading-spinner")], []),
						onSome: (record) => recordCard(h, model, shared, selected, record),
					}),
			memoryView(h, model, shared),
			Option.match(model.payload, {
				onNone: () => h.empty,
				onSome: (capture) => capturedView(h, capture),
			}),
		],
	);

export const view = defineView<Model, Message, Shared>((model, shared, h) =>
	h.div(
		[
			h.DataAttribute("testid", "page-Capture"),
			h.Class("space-y-4 max-w-2xl mx-auto"),
		],
		[
			h.h1([h.Class("text-lg font-bold")], [t("title")]),
			Option.match(model.error, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.div(
						[h.Role("alert"), h.Class("alert alert-error")],
						[
							h.span([], [error]),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-sm btn-ghost"),
									h.AriaLabel(t("dismissError")),
									h.OnClick(Message.ClickedDismissError()),
								],
								["✕"],
							),
						],
					),
			}),
			model.resolving
				? h.p(
						[h.Role("status"), h.Class("flex items-center gap-2")],
						[
							h.span([h.Class("loading loading-spinner h-4 w-4")], []),
							t("resolving"),
						],
					)
				: Option.match(model.selected, {
						onNone: () => unselectedView(h, model),
						onSome: (selected) => selectedView(h, model, shared, selected),
					}),
		],
	),
);
